import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Spinner,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AlertRegular } from '@fluentui/react-icons';
import { getStaffNotifications, markStaffNotificationRead } from '../services/api';
import { realLinkProps } from './links/realLinkProps';
import type { StaffNotification } from '../types';

/**
 * The bell that makes staff notifications readable.
 *
 * <p>PR #84 shipped the writer for `staffNotifications`. Nothing shipped a reader, so on
 * 2026-08-27 the container held 26 rows for a single preparer — 13 of them titled "... NOT
 * attached to a return" — every one of them unread, because no endpoint and no component could
 * fetch them. A client uploaded documents twice, the system correctly diagnosed both times that
 * half of them had landed unattached, and said so into a void. This component is the void's
 * other end.</p>
 */

const POLL_INTERVAL_MS = 60_000;

/** The writer marks an unattached upload in the TITLE; that phrase is the flag. */
function isUnattached(n: StaffNotification): boolean {
  return n.title.includes('NOT attached to a return');
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const useStyles = makeStyles({
  surface: { padding: 0, maxWidth: '420px', minWidth: '360px' },
  header: {
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontWeight: tokens.fontWeightSemibold,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    maxHeight: '420px',
    overflowY: 'auto',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  unread: { backgroundColor: tokens.colorNeutralBackground2 },
  title: {
    textAlign: 'left',
    textDecorationLine: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
  },
  body: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 },
  when: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  empty: { padding: '24px 16px', color: tokens.colorNeutralForeground2, textAlign: 'center' },
});

export default function NotificationBell() {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['staffNotifications'],
    queryFn: () => getStaffNotifications({ limit: 50 }),
    refetchInterval: POLL_INTERVAL_MS,
  });

  const items = data?.items ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  const markRead = useMutation({
    mutationFn: (id: string) => markStaffNotificationRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staffNotifications'] }),
  });

  const label =
    unreadCount > 0
      ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
      : 'No unread notifications';

  // Real links (fleet ruling 2026-10-29). An unattached upload is not on the return, so sending
  // the reader to the return would show them nothing -- Client Uploads is where it can actually
  // be reassigned. undefined (no taxReturnId, not unattached) leaves the title a plain button.
  const getNotificationHref = (n: StaffNotification): string | undefined =>
    isUnattached(n) ? '/client-uploads' : n.taxReturnId ? `/tax-returns/${n.taxReturnId}` : undefined;

  // KNOWN FOLLOW-UP (step 5 of the real-links port): marking read is a side effect of CLICKING
  // the notification, so it only runs in the tab that clicked. A reader who opens the same
  // notification in a new tab (or via a pasted/bookmarked link) lands on /tax-returns/:id or
  // /client-uploads with the notification still unread -- there is no notification id on that
  // route to key an on-mount mark-read off, and adding one risks double-firing against the
  // in-tab click. Left unfixed on purpose, same as the CRM's own documented bell-in-new-tab case.
  const handleFollow = (n: StaffNotification) => {
    // Idempotent server-side too, but not re-issuing the write keeps the original read instant
    // meaningful and avoids a pointless round-trip on every glance.
    if (!n.readAt) markRead.mutate(n.id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(_, d) => setOpen(d.open)}>
      <PopoverTrigger disableButtonEnhancement>
        <Tooltip content={label} relationship="label">
          <Button
            appearance="transparent"
            aria-label={label}
            icon={<AlertRegular />}
            style={{ color: 'var(--tl-header-fg)' }}
          >
            {unreadCount > 0 && (
              <Badge appearance="filled" color="danger" size="small">
                {unreadCount}
              </Badge>
            )}
          </Button>
        </Tooltip>
      </PopoverTrigger>

      <PopoverSurface className={styles.surface}>
        <div className={styles.header}>Notifications</div>

        {isLoading ? (
          <div className={styles.empty}>
            <Spinner size="tiny" label="Loading notifications..." />
          </div>
        ) : items.length === 0 ? (
          <div className={styles.empty}>No notifications yet.</div>
        ) : (
          <ul className={styles.list}>
            {items.map(n => (
              <li
                key={n.id}
                className={`${styles.item} ${n.readAt ? '' : styles.unread}`}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getNotificationHref(n) ? (
                    <a
                      className={styles.title}
                      {...realLinkProps(getNotificationHref(n)!, {
                        navigate,
                        onFollow: () => handleFollow(n),
                      })}
                    >
                      {n.title}
                    </a>
                  ) : (
                    <button
                      type="button"
                      className={styles.title}
                      onClick={() => handleFollow(n)}
                    >
                      {n.title}
                    </button>
                  )}
                  {isUnattached(n) && (
                    <Badge appearance="filled" color="danger" size="small">
                      Not attached
                    </Badge>
                  )}
                </div>
                {n.body && <div className={styles.body}>{n.body}</div>}
                <div className={styles.when}>{formatWhen(n.createdAt)}</div>
              </li>
            ))}
          </ul>
        )}
      </PopoverSurface>
    </Popover>
  );
}
